import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Building2, Plus, Edit, Trash2, Check, X, Loader2, ExternalLink,
  Eye, Star, Shield, Clock, DollarSign, Users, Globe, Handshake
} from 'lucide-react';

const tierLabels = {
  referral: { label: 'Referral (10%)', color: 'bg-blue-100 text-blue-700' },
  embedded: { label: 'Embedded (15%)', color: 'bg-green-100 text-green-700' },
  white_label: { label: 'White-Label (20-30%)', color: 'bg-purple-100 text-purple-700' },
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  rejected: 'bg-gray-100 text-gray-700',
};

export default function AdminMarketplacePage({ embedded = false }) {
  const { api } = useAuth();
  const [partners, setPartners] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('partners');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [listingDialogOpen, setListingDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingListing, setEditingListing] = useState(null);

  const [partnerForm, setPartnerForm] = useState({
    company_name: '', company_description: '', logo_url: '', website: '',
    category: '', contact_name: '', contact_email: '', contact_phone: '',
    address: '', city: '', state: '', zip_code: '', service_area: '',
    tier: 'referral', commission_rate: 10, approval_status: 'approved',
    compete_restriction: '', featured: false,
  });

  const [listingForm, setListingForm] = useState({
    partner: '', title: '', description: '', short_description: '',
    category: '', listing_type: 'referral', price: '', original_price: '',
    price_period: 'per_month', referral_url: '', grade_range: '',
    age_group: 'all', location_description: '', schedule_description: '',
    highlights: '', enrollment_open: true, status: 'approved',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, lRes] = await Promise.all([
        api.get('/marketplace/partners/'),
        api.get('/marketplace/listings/'),
      ]);
      setPartners(pRes.data.results || pRes.data || []);
      setListings(lRes.data.results || lRes.data || []);
    } catch (err) {
      console.error('Failed to fetch marketplace data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Partner CRUD
  const openPartnerDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setPartnerForm({
        company_name: partner.company_name || '',
        company_description: partner.company_description || '',
        logo_url: partner.logo_url || '',
        website: partner.website || '',
        category: partner.category || '',
        contact_name: partner.contact_name || '',
        contact_email: partner.contact_email || '',
        contact_phone: partner.contact_phone || '',
        address: partner.address || '',
        city: partner.city || '',
        state: partner.state || '',
        zip_code: partner.zip_code || '',
        service_area: partner.service_area || '',
        tier: partner.tier || 'referral',
        commission_rate: partner.commission_rate || 10,
        approval_status: partner.approval_status || 'approved',
        compete_restriction: partner.compete_restriction || '',
        featured: partner.featured || false,
      });
    } else {
      setEditingPartner(null);
      setPartnerForm({
        company_name: '', company_description: '', logo_url: '', website: '',
        category: '', contact_name: '', contact_email: '', contact_phone: '',
        address: '', city: '', state: '', zip_code: '', service_area: '',
        tier: 'referral', commission_rate: 10, approval_status: 'approved',
        compete_restriction: '', featured: false,
      });
    }
    setDialogOpen(true);
  };

  const savePartner = async () => {
    if (!partnerForm.company_name || !partnerForm.contact_email) {
      toast.error('Company name and contact email are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...partnerForm, commission_rate: parseFloat(partnerForm.commission_rate) || 10 };
      if (editingPartner) {
        await api.put(`/marketplace/partners/${editingPartner.id}/`, payload);
        toast.success('Partner updated!');
      } else {
        await api.post('/marketplace/partners/', payload);
        toast.success('Partner added!');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  const approvePartner = async (id) => {
    try {
      await api.patch(`/marketplace/partners/${id}/approve/`);
      toast.success('Partner approved!');
      fetchData();
    } catch { toast.error('Failed to approve'); }
  };

  const suspendPartner = async (id) => {
    if (!window.confirm('Suspend this partner? Their listings will be hidden.')) return;
    try {
      await api.patch(`/marketplace/partners/${id}/suspend/`);
      toast.success('Partner suspended');
      fetchData();
    } catch { toast.error('Failed to suspend'); }
  };

  const deletePartner = async (id) => {
    if (!window.confirm('Delete this partner permanently?')) return;
    try {
      await api.delete(`/marketplace/partners/${id}/`);
      toast.success('Partner deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  // Listing CRUD
  const openListingDialog = (listing = null) => {
    if (listing) {
      setEditingListing(listing);
      setListingForm({
        partner: listing.partner || '',
        title: listing.title || '',
        description: listing.description || '',
        short_description: listing.short_description || '',
        category: listing.category || '',
        listing_type: listing.listing_type || 'referral',
        price: listing.price || '',
        original_price: listing.original_price || '',
        price_period: listing.price_period || 'per_month',
        referral_url: listing.referral_url || '',
        grade_range: listing.grade_range || '',
        age_group: listing.age_group || 'all',
        location_description: listing.location_description || '',
        schedule_description: listing.schedule_description || '',
        highlights: (listing.highlights || []).join('\n'),
        enrollment_open: listing.enrollment_open ?? true,
        status: listing.status || 'approved',
      });
    } else {
      setEditingListing(null);
      setListingForm({
        partner: partners.length > 0 ? partners[0].id : '',
        title: '', description: '', short_description: '',
        category: '', listing_type: 'referral', price: '', original_price: '',
        price_period: 'per_month', referral_url: '', grade_range: '',
        age_group: 'all', location_description: '', schedule_description: '',
        highlights: '', enrollment_open: true, status: 'approved',
      });
    }
    setListingDialogOpen(true);
  };

  const saveListing = async () => {
    if (!listingForm.title || !listingForm.partner) {
      toast.error('Title and partner are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...listingForm,
        price: parseFloat(listingForm.price) || 0,
        original_price: listingForm.original_price ? parseFloat(listingForm.original_price) : null,
        highlights: listingForm.highlights.split('\n').filter(Boolean),
      };
      if (editingListing) {
        await api.put(`/marketplace/listings/${editingListing.id}/`, payload);
        toast.success('Listing updated!');
      } else {
        await api.post('/marketplace/listings/', payload);
        toast.success('Listing created!');
      }
      setListingDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save listing');
    } finally {
      setSaving(false);
    }
  };

  const reviewListing = async (id, newStatus) => {
    try {
      await api.patch(`/marketplace/listings/${id}/review/`, { status: newStatus });
      toast.success(`Listing ${newStatus}!`);
      fetchData();
    } catch { toast.error('Failed to update listing'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Handshake className="w-6 h-6" /> Marketplace Partners
          </h1>
          <p className="text-muted-foreground">Manage education partners like SAM, Mathnasium, RSM, etc. who list programs on your platform.</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{partners.length}</p>
          <p className="text-sm text-muted-foreground">Total Partners</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{partners.filter(p => p.approval_status === 'approved').length}</p>
          <p className="text-sm text-muted-foreground">Approved</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{partners.filter(p => p.approval_status === 'pending').length}</p>
          <p className="text-sm text-muted-foreground">Pending</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{listings.length}</p>
          <p className="text-sm text-muted-foreground">Total Listings</p>
        </CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="partners">Partners ({partners.length})</TabsTrigger>
          <TabsTrigger value="listings">Program Listings ({listings.length})</TabsTrigger>
        </TabsList>

        {/* Partners Tab */}
        <TabsContent value="partners" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openPartnerDialog()}>
              <Plus className="h-4 w-4 mr-2" /> Add Partner
            </Button>
          </div>

          {partners.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-semibold">No marketplace partners yet</p>
              <p className="text-sm">Add your first partner like SAM - Singapore Math, Mathnasium, etc.</p>
              <Button className="mt-4" onClick={() => openPartnerDialog()}><Plus className="h-4 w-4 mr-2" /> Add Partner</Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {partners.map(p => (
                <Card key={p.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        {p.logo_url ? (
                          <img src={p.logo_url} alt="" className="h-12 w-12 rounded-lg object-contain border" />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{p.company_name}</h3>
                            {p.featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1">{p.company_description}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge className={statusColors[p.approval_status] || 'bg-gray-100'}>{p.approval_status}</Badge>
                            <Badge className={tierLabels[p.tier]?.color || 'bg-gray-100'}>{tierLabels[p.tier]?.label || p.tier}</Badge>
                            {p.category && <Badge variant="outline">{p.category}</Badge>}
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />{p.commission_rate}% commission
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{p.contact_name} · {p.contact_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {p.website && (
                          <a href={p.website} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="icon"><Globe className="h-4 w-4" /></Button>
                          </a>
                        )}
                        {p.approval_status === 'pending' && (
                          <Button variant="ghost" size="icon" onClick={() => approvePartner(p.id)} className="text-green-600">
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        {p.approval_status === 'approved' && (
                          <Button variant="ghost" size="icon" onClick={() => suspendPartner(p.id)} className="text-amber-600">
                            <Shield className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openPartnerDialog(p)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deletePartner(p.id)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Listings Tab */}
        <TabsContent value="listings" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openListingDialog()} disabled={partners.length === 0}>
              <Plus className="h-4 w-4 mr-2" /> Add Listing
            </Button>
          </div>

          {listings.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              <Eye className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-semibold">No program listings yet</p>
              <p className="text-sm">{partners.length === 0 ? 'Add a partner first, then create listings.' : 'Create a program listing for one of your partners.'}</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {listings.map(l => (
                <Card key={l.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{l.title}</h3>
                          <Badge className={statusColors[l.status] || 'bg-gray-100'}>{l.status}</Badge>
                          <Badge variant="outline" className="text-xs">{l.listing_type === 'referral' ? 'Referral' : l.listing_type === 'embedded' ? 'Embedded' : 'White-Label'}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{l.partner_name} · {l.category} · ${parseFloat(l.price || 0).toFixed(0)}/{l.price_period?.replace('per_', '')}</p>
                      </div>
                      <div className="flex gap-1">
                        {l.status === 'pending_review' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => reviewListing(l.id, 'approved')} className="text-green-600"><Check className="h-3 w-3 mr-1" />Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => reviewListing(l.id, 'rejected')} className="text-red-600"><X className="h-3 w-3 mr-1" />Reject</Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openListingDialog(l)}><Edit className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Partner Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add Marketplace Partner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Company Name *</Label>
                <Input value={partnerForm.company_name} onChange={e => setPartnerForm({...partnerForm, company_name: e.target.value})} placeholder="e.g., SAM - Singapore Math" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea value={partnerForm.company_description} onChange={e => setPartnerForm({...partnerForm, company_description: e.target.value})} placeholder="About this partner..." rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={partnerForm.category} onChange={e => setPartnerForm({...partnerForm, category: e.target.value})} placeholder="e.g., Math Enrichment" />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input value={partnerForm.website} onChange={e => setPartnerForm({...partnerForm, website: e.target.value})} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input value={partnerForm.logo_url} onChange={e => setPartnerForm({...partnerForm, logo_url: e.target.value})} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>Service Area</Label>
                <Input value={partnerForm.service_area} onChange={e => setPartnerForm({...partnerForm, service_area: e.target.value})} placeholder="e.g., Aurora, Naperville" />
              </div>
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input value={partnerForm.contact_name} onChange={e => setPartnerForm({...partnerForm, contact_name: e.target.value})} placeholder="John Doe" />
              </div>
              <div className="space-y-2">
                <Label>Contact Email *</Label>
                <Input value={partnerForm.contact_email} onChange={e => setPartnerForm({...partnerForm, contact_email: e.target.value})} placeholder="john@partner.com" />
              </div>
              <div className="space-y-2">
                <Label>Partnership Tier</Label>
                <Select value={partnerForm.tier} onValueChange={v => setPartnerForm({...partnerForm, tier: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="referral">Referral (10% — redirect to partner site)</SelectItem>
                    <SelectItem value="embedded">Embedded (15% — enroll on your platform)</SelectItem>
                    <SelectItem value="white_label">White-Label (20-30% — your brand)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Commission Rate (%)</Label>
                <Input type="number" value={partnerForm.commission_rate} onChange={e => setPartnerForm({...partnerForm, commission_rate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={partnerForm.approval_status} onValueChange={v => setPartnerForm({...partnerForm, approval_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={savePartner} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingPartner ? 'Save Changes' : 'Add Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Listing Dialog */}
      <Dialog open={listingDialogOpen} onOpenChange={setListingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingListing ? 'Edit Program Listing' : 'Add Program Listing'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Partner *</Label>
                <Select value={listingForm.partner} onValueChange={v => setListingForm({...listingForm, partner: v})}>
                  <SelectTrigger><SelectValue placeholder="Select partner..." /></SelectTrigger>
                  <SelectContent>
                    {partners.filter(p => p.approval_status === 'approved').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Program Title *</Label>
                <Input value={listingForm.title} onChange={e => setListingForm({...listingForm, title: e.target.value})} placeholder="e.g., Singapore Math — Problem Solving Level 3" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Short Description</Label>
                <Input value={listingForm.short_description} onChange={e => setListingForm({...listingForm, short_description: e.target.value})} placeholder="One-line summary" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Full Description</Label>
                <Textarea value={listingForm.description} onChange={e => setListingForm({...listingForm, description: e.target.value})} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={listingForm.category} onChange={e => setListingForm({...listingForm, category: e.target.value})} placeholder="e.g., Math Enrichment" />
              </div>
              <div className="space-y-2">
                <Label>Listing Type</Label>
                <Select value={listingForm.listing_type} onValueChange={v => setListingForm({...listingForm, listing_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="referral">Referral (redirect to partner)</SelectItem>
                    <SelectItem value="embedded">Embedded (enroll here)</SelectItem>
                    <SelectItem value="white_label">White-Label (your brand)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Price</Label>
                <Input type="number" step="0.01" value={listingForm.price} onChange={e => setListingForm({...listingForm, price: e.target.value})} placeholder="149.99" />
              </div>
              <div className="space-y-2">
                <Label>Price Period</Label>
                <Select value={listingForm.price_period} onValueChange={v => setListingForm({...listingForm, price_period: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_month">Per Month</SelectItem>
                    <SelectItem value="per_session">Per Session</SelectItem>
                    <SelectItem value="per_semester">Per Semester</SelectItem>
                    <SelectItem value="one_time">One Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {listingForm.listing_type === 'referral' && (
                <div className="space-y-2 col-span-2">
                  <Label>Referral URL</Label>
                  <Input value={listingForm.referral_url} onChange={e => setListingForm({...listingForm, referral_url: e.target.value})} placeholder="https://partner-site.com/register?ref=rsc" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Grade Range</Label>
                <Input value={listingForm.grade_range} onChange={e => setListingForm({...listingForm, grade_range: e.target.value})} placeholder="e.g., K-5, 6-8" />
              </div>
              <div className="space-y-2">
                <Label>Age Group</Label>
                <Select value={listingForm.age_group} onValueChange={v => setListingForm({...listingForm, age_group: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ages</SelectItem>
                    <SelectItem value="young">Ages 5-9</SelectItem>
                    <SelectItem value="tween">Ages 10-13</SelectItem>
                    <SelectItem value="teen">Ages 14-18</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Highlights (one per line)</Label>
                <Textarea value={listingForm.highlights} onChange={e => setListingForm({...listingForm, highlights: e.target.value})} rows={3} placeholder="Singapore Math methodology&#10;Small class sizes (max 8)&#10;Weekly progress reports" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={listingForm.location_description} onChange={e => setListingForm({...listingForm, location_description: e.target.value})} placeholder="e.g., Aurora, IL" />
              </div>
              <div className="space-y-2">
                <Label>Schedule</Label>
                <Input value={listingForm.schedule_description} onChange={e => setListingForm({...listingForm, schedule_description: e.target.value})} placeholder="e.g., Mon/Wed 4-5pm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setListingDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveListing} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingListing ? 'Save Changes' : 'Create Listing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
